import React, {
    BaseHTMLAttributes,
    ButtonHTMLAttributes,
    DetailedHTMLProps,
    useState,
} from 'react';
import { Button, Tooltip } from 'antd';
import Image from 'next/image';
import ib from './ItemButtons.module.scss';

import icon_compare_default from '/public/static/images/icon_compare_default.svg';
import icon_compare_hovered from '/public/static/images/icon_compare_hovered.svg';
import icon_compare_pressed from '/public/static/images/icon_compare_pressed.svg';
import icon_favourite_default from '/public/static/images/icon_favourite_default.svg';
import icon_favourite_hovered from '/public/static/images/icon_favourite_hovered.svg';
import icon_favourite_pressed from '/public/static/images/icon_favourite_pressed.svg';
import icon_3dmodel_default from '/public/static/images/icon_3dmodel_default.svg';
import icon_3dmodel_hovered from '/public/static/images/icon_3dmodel_hovered.svg';
import { ButtonProps, Typography } from 'antd';
import { TCustomIcon } from 'tkmp-lib/src/interfaces';
import Link from 'next/link';
import useLocalization from 'tkmp-lib/src/hooks/useLocalization';

type TImageButtonStates = {
    default: any;
    hovered?: any;
    pressed?: any;
};

interface IImageButton {
    active: boolean;
    icon?: any;
    tooltipTitle?: any;
    buttonStates?: TImageButtonStates;
    text?: string;
}
interface ITransparentButton
    extends DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
    IconComponent: TCustomIcon;
    onClick?: (e?: any) => any;
    childrenStyle?: any;
    title?: string;
    width?: string | number;
    height?: string | number;
}

const CustomButton = ({
    isPressed,
    text,
    tooltipTitle,
    icon,
    hoveredImg,
    pressedImg,
    defaultImg,
    ...props
}: any) => {
    const [isHovered, setIsHovered] = useState<boolean>(false);

    return (
        <Button
            onClick={() => props?.onClick}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={ib.button}
            style={text ? { aspectRatio: 'unset' } : {}}
            type={'text'}
            {...props}
        >
            {!text && isHovered && tooltipTitle && <div className={ib.tooltip}>{tooltipTitle}</div>}
            <Image
                src={icon ? icon : isHovered ? hoveredImg : isPressed ? pressedImg : defaultImg}
                alt={''}
                width={props?.width ?? 0}
                height={props?.height ?? 0}
                sizes={'100%'}
            />
            {text && (
                <Typography.Text className={`text-14-400 text-[#0B0B0C]`}>{text}</Typography.Text>
            )}
        </Button>
    );
};

export const CustomTransparentButton = ({
    IconComponent,
    onClick,
    width,
    height,
    title,
    childrenStyle,
    ...props
}: ITransparentButton) => {
    const [isHovered, setIsHovered] = useState<boolean>(false);

    const styleButton: any = { width: '40px', height: '40px' };
    if (width) styleButton.width = width;
    if (height) styleButton.height = height;

    let styleChildren = {};
    if (childrenStyle) styleChildren = { ...childrenStyle };
    if (title)
        styleChildren = {
            ...styleChildren,
            display: 'flex',
            flexDirection: 'row',
            gap: '8px',
            alignContent: 'center',
            whiteSpace: 'nowrap',
        };

    return (
        <button
            className={`${ib.CustomTransparentButton} ${title ? ib.text : ''}`}
            style={styleButton}
            onClick={onClick ? onClick : undefined}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            {...props}
        >
            <div style={styleChildren}>
                <IconComponent active={isHovered} />
                {!!title && (
                    <span
                        className={`text-16-400`}
                        style={{ color: '#FFFFFF', alignContent: 'center' }}
                    >
                        {title}
                    </span>
                )}
            </div>
        </button>
    );
};
export const CustomTransparentLink = ({
    IconComponent,
    href,
    width,
    height,
    noStyle,
    ...props
}: any) => {
    const [isHovered, setIsHovered] = useState<boolean>(false);

    return (
        <Link
            className={noStyle ? ib.CustomTransparentLink : ib.CustomTransparentButton}
            style={
                noStyle
                    ? {}
                    : {
                          width: width ?? 40,
                          height: height ?? 40,
                      }
            }
            href={href}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            passHref={noStyle}
        >
            {noStyle ? (
                <Button
                    type={'text'}
                    className={ib.CustomTransparentLink_Button}
                >
                    <IconComponent active={isHovered} />
                    {props?.children && props.children}
                </Button>
            ) : (
                <>
                    <IconComponent active={isHovered} />
                    {props?.children && <span className={`text-16-400`}>{props.children}</span>}
                </>
            )}
        </Link>
    );
};

export const ImageButton = ({
    active,
    tooltipTitle,
    icon,
    text,
    buttonStates,
    ...props
}: (IImageButton & BaseHTMLAttributes<any>) | any) => (
    <CustomButton
        isPressed={active}
        icon={icon}
        tooltipTitle={tooltipTitle}
        defaultImg={buttonStates?.default}
        pressedImg={buttonStates?.pressed ?? buttonStates?.default}
        hoveredImg={buttonStates?.hovered ?? buttonStates?.pressed ?? buttonStates?.default}
        {...props}
    />
);

export const CompareButton = ({ active, ...props }: any) => {
    const LOCALIZATION = useLocalization();
    return (
        <CustomButton
            tooltipTitle={
                active
                    ? LOCALIZATION.pages.product.rightCard.compareButton.true
                    : LOCALIZATION.pages.product.rightCard.compareButton.false
            }
            isPressed={active}
            defaultImg={icon_compare_default}
            pressedImg={icon_compare_pressed}
            hoveredImg={icon_compare_hovered}
            {...props}
        />
    );
};

export const FavouriteButton = ({ active, ...props }: any) => {
    const LOCALIZATION = useLocalization();
    return (
        <CustomButton
            tooltipTitle={
                active
                    ? LOCALIZATION.pages.product.rightCard.favouriteButton.true
                    : LOCALIZATION.pages.product.rightCard.favouriteButton.false
            }
            isPressed={active}
            defaultImg={icon_favourite_default}
            pressedImg={icon_favourite_pressed}
            hoveredImg={icon_favourite_hovered}
            {...props}
        />
    );
};

export const ModelButton = ({ active, ...props }: any) => (
    <CustomButton
        tooltipTitle={'asd'}
        isPressed={active}
        defaultImg={icon_3dmodel_default}
        hoveredImg={icon_3dmodel_hovered}
        {...props}
    />
);
